import Link from "@/src/common/components/Link"
import { Disclosure } from "@headlessui/react"
import Image from "next/legacy/image"
import { Fragment } from "react"

import { GENDER } from "../../business-layer/constants"
import { CandidateInfo } from "../../business-layer/types"
import Avatar from "../Avatar"
import Icon from "../Icon"
import Typography from "../Typography"

export type CandidateProfileProps = { className?: string } & Omit<
  CandidateInfo,
  "slug"
>

const Bio = ({ name, bio }: Pick<CandidateProfileProps, "name" | "bio">) => (
  <article id="candidate-bio" className="p-0 pt-20 md:p-16">
    <section className="mx-auto">
      {name ? (
        <Typography
          type="p15"
          weight="bold"
          className="uppercase text-black text-center"
        >
          {name}
        </Typography>
      ) : null}
      {bio?.website ? (
        <div className="flex flex-row flex-wrap mx-auto justify-center">
          <Typography
            type="p15"
            weight="bold"
            className="text-black mr-2 inline-block"
          >
            Website:
          </Typography>
          <Link legacyBehavior href={bio?.website}>
            <a
              className={Typography.className({
                type: "p15",
                className: "text-blue-600 underline"
              })}
            >
              {bio?.website}
            </a>
          </Link>
        </div>
      ) : null}
    </section>
    <ul className="mt-6">
      {[
        ["Core mandates", bio?.objective],
        ["Background and family life", bio?.background],
        ["Work experience", bio?.workExperience],
        ["Political offices", bio?.offices],
        ["Education", bio?.education],
        ["Notable mentions", bio?.notableMentions],
        ["Read more about " + name, bio?.readMore]
      ]
        .filter(([_, content]) => Boolean(content))
        .map(([title, content]) =>
          content ? (
            <li key={title as string} className="mt-4">
              <Typography type="p15" weight="bold" className="text-black">
                {title}
              </Typography>
              {Array.isArray(content) ? (
                <ol className="list-disc pl-4 md:pl-10">
                  {content.map((line, idx) => {
                    const contentLine = line as any
                    return (
                      <li key={idx}>
                        {contentLine && contentLine.url && contentLine.text ? (
                          <Link legacyBehavior href={contentLine.url}>
                            <a
                              className={Typography.className({
                                type: "p15",
                                className: "text-blue-600 underline"
                              })}
                            >
                              {contentLine.text}
                            </a>
                          </Link>
                        ) : (
                          <Typography type="p15" className="text-black">
                            {line}
                          </Typography>
                        )}
                      </li>
                    )
                  })}
                </ol>
              ) : (
                <Typography type="p15" className="text-black">
                  {content}
                </Typography>
              )}
            </li>
          ) : null
        )}
    </ul>
  </article>
)

const CandidateProfile = ({
  name,
  age,
  gender,
  photoURL,
  partyLogoURL,
  party,
  bio,
  className: extraClassNames = ""
}: CandidateProfileProps) => {
  const profileItems = [
    {
      name: "Age",
      value: age ? age.toString() : "N/A"
    },
    { name: "Gender", value: gender ? GENDER[gender] : "N/A" },
    {
      name: "Current political party",
      value: party,
      icon: partyLogoURL
    }
  ]
  const profileRows: Array<
    Array<{ name: string; value: string; icon?: string }>
  > = []
  const chunkSize = 2
  for (let i = 0; i < profileItems.length; i += chunkSize) {
    profileRows.push(profileItems.slice(i, i + chunkSize))
  }
  return (
    <>
      <section className="-md:hidden">
        <article className="mt-4 border border-gray-10 bg-white">
          <section className="p-6 w-full flex flex-row justify-start">
            <span className="mr-3">
              <Avatar size="md" src={photoURL || undefined} />
            </span>
            <Typography
              type="p15x"
              weight="medium"
              className="my-auto text-left"
            >
              {name}
            </Typography>
          </section>
          <div className="mx-6 block h-0 border-b border-gray-200" />
          <ol className="w-full p-6 list-none flex flex-row justify-stretch">
            {profileItems.map(({ name, value, icon }) => (
              <li key={name} className="w-full">
                <Typography type="p12x" className="text-gray-600">
                  {name}
                </Typography>
                <span className="flex flex-row align-top">
                  {icon && (
                    <div className="mr-1 mt-2 h-4 w-4 block">
                      <Image
                        src={icon}
                        alt={value}
                        width={16}
                        height={16}
                        layout="responsive"
                        className="h-4 w-4"
                      />
                    </div>
                  )}
                  <Typography type="p15x" className="text-gray-800 mt-2">
                    {value}
                  </Typography>
                </span>
              </li>
            ))}
          </ol>
        </article>
        {bio && Object.keys(bio).length !== 0 ? (
          <Bio name={name} bio={bio} />
        ) : null}
      </section>
      <section className="md:hidden">
        <Disclosure defaultOpen={true}>
          {({ open }) => (
            <>
              <Disclosure.Button
                className={`p-4 w-full flex flex-row border border-gray-200 bg-white ${extraClassNames} ${
                  open ? "shadow-sm" : ""
                }`}
              >
                <>
                  <span className="mr-3">
                    <Avatar size="md" src={photoURL || undefined} />
                  </span>
                  <Typography
                    type="p15"
                    weight="medium"
                    className="my-auto text-left"
                  >
                    {name}
                  </Typography>
                  <span className="mr-0 ml-auto my-auto">
                    <Icon.Chevron open={open} size="sm" color="black" />
                  </span>
                </>
              </Disclosure.Button>
              <Disclosure.Panel className="w-full mt-2 shadow-sm flex flex-col p-6 border border-gray-200 bg-white">
                <section>
                  {profileRows.map((profileRow, idx) => (
                    <Fragment key={idx}>
                      {idx === 0 ? null : (
                        <div className="w-full my-6 block h-0 border-b border-gray-200" />
                      )}
                      <ol className="w-full list-none flex flex-row justify-stretch">
                        {profileRow.map(({ name, value, icon }) => (
                          <li key={name} className="w-full">
                            <Typography type="p12" className="text-gray-600">
                              {name}
                            </Typography>
                            <span className="flex flex-row align-top">
                              {icon ? (
                                <div className="mr-1 mt-2 h-4 w-4 block">
                                  <Image
                                    src={icon}
                                    alt={value}
                                    width={16}
                                    height={16}
                                    layout="responsive"
                                    className="h-4 w-4"
                                  />
                                </div>
                              ) : null}
                              <Typography
                                type="p15x"
                                className="text-gray-800 mt-2"
                                weight="medium"
                              >
                                {value}
                              </Typography>
                            </span>
                          </li>
                        ))}
                      </ol>
                    </Fragment>
                  ))}
                </section>
                <section>
                  <Bio name={name} bio={bio} />
                </section>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </section>
    </>
  )
}

export default CandidateProfile
